import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import SEOHead from '@/components/SEOHead';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ─── /confirm-email?token=… ──────────────────────────────────────────────────
// The link sent to a new email address from Settings → Profile. Opening it is
// what changes the address. Works signed out too: the token is the credential.

export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const { session, refreshProfile } = useAuth();
  const [state, setState] = useState({ status: 'working', message: '' });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get('token');
    if (!token) { setState({ status: 'error', message: 'This link is incomplete.' }); return; }

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/change-email/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'This link is invalid or has expired.');
        // Signed in here: swap to the token that names the new address.
        if (session?.access_token && data.access_token && data.user) {
          await supabase.auth.setSession({ access_token: data.access_token, user: data.user });
          refreshProfile?.().catch(() => {});
        }
        setState({ status: 'done', message: data.user?.email || '' });
      } catch (err) {
        setState({ status: 'error', message: err.message });
      }
    })();
  }, [params, session, refreshProfile]);

  return (
    <div className="min-h-screen bg-zinc-950 pt-28 pb-16 px-4">
      <SEOHead title="Confirm your email" noIndex />
      <div className="max-w-md mx-auto rounded-3xl border border-zinc-800 bg-zinc-900 p-8 text-center">
        {state.status === 'working' && (
          <>
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto" />
            <p className="mt-4 text-zinc-400 text-sm">Confirming your new email address…</p>
          </>
        )}
        {state.status === 'done' && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-white">Email address changed</h1>
            <p className="mt-2 text-zinc-400 text-sm">
              {state.message ? <>You now sign in with <span className="text-white font-medium">{state.message}</span>.</> : 'You can sign in with your new address.'}
            </p>
            <Link to={session ? '/Settings' : '/'} className="mt-6 inline-flex h-10 items-center px-5 rounded-full bg-white text-zinc-950 text-sm font-semibold">
              {session ? 'Back to settings' : 'Sign in'}
            </Link>
          </>
        )}
        {state.status === 'error' && (
          <>
            <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
            <h1 className="mt-4 text-xl font-bold text-white">That link did not work</h1>
            <p className="mt-2 text-zinc-400 text-sm">{state.message}</p>
            <Link to="/Settings" className="mt-6 inline-flex h-10 items-center px-5 rounded-full bg-white text-zinc-950 text-sm font-semibold">
              Go to settings
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
